import { useEffect, useRef, useState } from 'react'
import React from 'react'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'

import classnames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import Image from '~/components/atoms/Image/Image'
import type { PDPValuationCtaProps } from '~/components/atoms/PDPValuationCta/PDPValuationCta'
import ProfileImage, {
  ProfileImageType,
} from '~/components/atoms/ProfileImage/ProfileImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import useMabPostHog, {
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import { useMediaQuery } from '~/services/useMediaQuery'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'
import capFirstLetter from '~/utils/capFirstLetter'
import { getLocalizedHardCodedSlug } from '~/utils/getLocalizedHardCodedSlug'
import { useScrollPosition } from '~/utils/useScrollPosition'

import type { StickySectionHeaderProps } from '../StickySectionHeader/StickySectionHeader'
import StickySectionHeader from '../StickySectionHeader/StickySectionHeader'

import initialStyles from './MagazineArticleExpert.module.css'
import endStyles from './MagazineArticleExpertEnd.module.css'

const sellregex = /seller[\u2019']?s guide/i
const buyregex = /buyer[\u2019']?s guide/i
const investmentGuideRegex = /investment[- ]?guide/i

export interface MagazineArticleExpertProps {
  testID?: string
  expert?: PDPValuationCtaProps['expert']
  artist?: {
    slug: string
    name: string
  }
  hideExpert?: boolean
  stickyLinkBar?: StickySectionHeaderProps
  isStandAlone?: boolean
  noMarginBottom?: boolean
  useStickyBehaviour?: boolean
  location: 'Header' | 'Footer'
}

export default function MagazineArticleExpert({
  testID,
  expert,
  artist,
  hideExpert,
  stickyLinkBar,
  isStandAlone = false,
  noMarginBottom = false,
  useStickyBehaviour = false,
  location,
}: MagazineArticleExpertProps) {
  const t = useTranslations('forms')
  const tCommon = useTranslations('common')
  const tValuation = useTranslations('valuation')
  const styles = isStandAlone ? endStyles : initialStyles
  const { mainHeaderVisible, isSearchOpen } = useInPageNavContext()
  const isMobile = useMediaQuery(`(max-width: 768px)`)
  // const isLargeDesktop = useMediaQuery(`(min-width: 1499px`)
  const isLargeDesktop = false
  const contentRef = useRef<any>(null)
  const locale = useLocale()

  const normalContentRef = useRef<any>(null)
  const tabletVersion = !isLargeDesktop || isStandAlone
  const { pushEvent } = useMabPostHog()

  const [showSticky, setShowSticky] = useState(false)
  // Once the inline copy's natural position is captured we collapse it so the
  // expert content only ever appears in its sticky state.
  const [measured, setMeasured] = useState(false)

  const normalContentBottomRef = useRef<number | null>(null)

  useEffect(() => {
    if (normalContentRef.current && !normalContentBottomRef.current) {
      const rect = normalContentRef.current.getBoundingClientRect()
      normalContentBottomRef.current = rect.bottom + window.scrollY
      setMeasured(true)
    }
  }, [])

  useScrollPosition(
    ({ currPos }) => {
      if (useStickyBehaviour && normalContentBottomRef.current !== null) {
        const currentScroll = currPos.y * -1

        setShowSticky(currentScroll >= normalContentBottomRef.current + 20)
      }
    },
    [useStickyBehaviour, showSticky],
    undefined,
    false,
    50
  )

  if (isStandAlone && isLargeDesktop) return null
  let interactionLocation = 'Articles'

  if (stickyLinkBar?.category) {
    interactionLocation = capFirstLetter(stickyLinkBar.category)
  }
  if (stickyLinkBar?.customTaxonomy) {
    interactionLocation = capFirstLetter(stickyLinkBar.customTaxonomy)
  }

  const renderPrimaryCta = () => {
    return (
      <Button
        variant={ButtonVariants.primaryDark}
        link={'/artworks'}
        iconLeft={!isMobile}
        button={{
          type: ButtonTypes.button,
          onClick: () => {
            analyticsPusher(analyticsEventTypes.gaEvent, {
              gaEventName: 'Browse Artworks article clicked',
            })
          },
        }}
        className={styles.ctaButton}
      >
        {tCommon('buyArt')}
      </Button>
    )
    // Current requirements have changed - but could be easily switched back, so leaving the logic in place for now

    // if (stickyLinkBar?.category === 'masterpieces') {
    //   return (
    //     <Button
    //       iconLeft={!isMobile}
    //       variant={ButtonVariants.primaryDark}
    //       button={{
    //         type: ButtonTypes.button,
    //         onClick: () => {
    //           analyticsPusher(analyticsEventTypes.gaEvent, {
    //             gaEventName: 'Request an appraisal article',
    //           })
    //         },
    //       }}
    //       launchForm={FormTypes.masterpiece}
    //       className={styles.ctaButton}
    //     >
    //       {t('requestAppraisal')}
    //     </Button>
    //   )
    // }

    // if (stickyLinkBar?.title && buyregex.test(stickyLinkBar?.title)) {
    //   return (
    //     <Button
    //       variant={ButtonVariants.primaryDark}
    //       link={'/artworks'}
    //       iconLeft={!isMobile}
    //       button={{
    //         type: ButtonTypes.button,
    //         onClick: () => {
    //           analyticsPusher(analyticsEventTypes.gaEvent, {
    //             gaEventName: 'Browse Artworks article clicked',
    //           })
    //         },
    //       }}
    //       className={styles.ctaButton}
    //     >
    //       {tCommon('browseAllArtwork')}
    //     </Button>
    //   )
    // }

    // return (
    //   <Button
    //     variant={ButtonVariants.primaryDark}
    //     iconLeft={!isMobile}
    //     button={{
    //       type: ButtonTypes.button,
    //       onClick: () => {
    //         analyticsPusher(analyticsEventTypes.gaEvent, {
    //           gaEventName: 'Need A Valuation Article',
    //         })
    //         pushEvent('Enquire for Valution Clicked', {
    //           webProduct: FormTypes.valuation,
    //           interactionType: PushInteractionType.Click,
    //           interactionLocation,
    //           interactionLocationDetail: `${location} CTA`,
    //         })
    //       },
    //     }}
    //     link={getLocalizedHardCodedSlug('valuations', locale)}
    //     className={styles.ctaButton}
    //   >
    //     {tValuation('instantValuation')}
    //   </Button>
    // )
  }

  const renderSecondaryCta = () => {
    // Current requirements have changed - but could be easily switched back, so leaving the logic in place for now
    // if (stickyLinkBar?.category === 'masterpieces') {
    //   return (
    //     <Button
    //       variant={
    //         !tabletVersion
    //           ? ButtonVariants.textLink
    //           : ButtonVariants.secondaryDark
    //       }
    //       iconLeft={!isMobile}
    //       underline={!tabletVersion}
    //       link={'/articles'}
    //       className={styles.ctaButton}
    //     >
    //       {tCommon('viewAllCategory', { category: tCommon('articles') })}
    //     </Button>
    //   )
    // }

    // if (
    //   stickyLinkBar?.title &&
    //   (investmentGuideRegex.test(stickyLinkBar?.title) ||
    //     buyregex.test(stickyLinkBar?.title))
    // ) {
    //   return (
    //     <Button
    //       variant={
    //         !tabletVersion
    //           ? ButtonVariants.textLink
    //           : ButtonVariants.secondaryDark
    //       }
    //       iconLeft={!isMobile}
    //       underline={!tabletVersion}
    //       link={'/artworks'}
    //       className={styles.ctaButton}
    //     >
    //       {tCommon('buyArt')}
    //     </Button>
    //   )
    // }

    // if (stickyLinkBar?.category === 'investing') {
    //   return (
    //     <Button
    //       variant={
    //         !tabletVersion
    //           ? ButtonVariants.textLink
    //           : ButtonVariants.secondaryDark
    //       }
    //       iconLeft={!isMobile}
    //       underline={!tabletVersion}
    //       link={'/artworks'}
    //       className={styles.ctaButton}
    //     >
    //       {tCommon('browseAllArtwork')}
    //     </Button>
    //   )
    // }

    return (
      <Button
        variant={
          !tabletVersion
            ? ButtonVariants.textLink
            : ButtonVariants.secondaryDark
        }
        iconLeft={!isMobile}
        underline={!tabletVersion}
        link={getLocalizedHardCodedSlug('sell', locale)}
        className={styles.ctaButton}
        linkOnclick={() => {
          pushEvent('Sell Art Clicked', {
            webProduct: PushWebProduct.Articles,
            interactionType: PushInteractionType.Click,
            interactionLocation,
            interactionLocationDetail: `${location} CTA`,
          })
        }}
      >
        {tCommon('sellArt')}
      </Button>
    )
  }

  if (isSearchOpen) return null

  const renderContent = (isSticky = false) => {
    // The inline copy is kept mounted as a measurement anchor, but once measured
    // we collapse it to zero height so nothing shows until the sticky copy appears.
    const isHiddenAnchor = !isSticky && useStickyBehaviour && measured

    return (
      <div
        ref={isSticky ? null : normalContentRef}
        className={classnames(styles.base, {
          [styles.stickyContainer]: isSticky && useStickyBehaviour,
          [styles.mainHeaderVisible]: mainHeaderVisible,
          [styles.hiddenAnchor]: isHiddenAnchor,
        })}
      >
        <div className={styles.base} data-testid={testID}>
          <div
            className={classnames(styles.sticky, {
              [styles.isSticky]: isSticky,
            })}
            ref={contentRef}
          >
            <div className={styles.content}>
              <Container>
                {!isSticky && expert && !hideExpert && (
                  <div className={styles.teamMemberContainer}>
                    <div className={styles.avatar}>
                      <Image
                        asset={expert.image.asset}
                        alt={expert.name}
                        width={60}
                      />
                    </div>
                    <div className={styles.teamMemberDetails}>
                      <Text
                        Tag="p"
                        size={TextSizes.bodySml}
                        className={classnames(styles.expert, styles.expertName)}
                      >
                        {expert.name}
                      </Text>
                      <Text
                        Tag="p"
                        size={TextSizes.bodySml}
                        className={styles.expert}
                      >
                        {expert.jobTitle}
                      </Text>
                      <Text
                        Tag="p"
                        size={TextSizes.bodySml}
                        className={styles.expert}
                      >
                        {expert.email ? (
                          <span
                            className={classnames(
                              styles.underline,
                              tabletVersion && styles.mobileEmail
                            )}
                          >
                            <Link href={`mailto: ${expert.email}`}>
                              {expert.email}
                            </Link>
                          </span>
                        ) : null}
                      </Text>
                    </div>
                  </div>
                )}
                <div className={styles.textContainer}>
                  {stickyLinkBar?.image && useStickyBehaviour && isMobile && (
                    <div className={styles.profileImage}>
                      <ProfileImage
                        name={stickyLinkBar.name}
                        image={stickyLinkBar.image}
                        showName={false}
                        _id={stickyLinkBar.id || ''}
                        inline={true}
                        smallImage={true}
                        type={ProfileImageType.Artist}
                      />
                    </div>
                  )}
                  <Text
                    Tag="p"
                    size={TextSizes.primaryTitleSml}
                    className={styles.title}
                  >
                    {t.rich('interestedInBuyingOrSellingGeneric', {
                      subject: artist ? artist.name : tCommon('work'),
                      br: chunks => <br />,
                    })}
                  </Text>
                </div>
                {useStickyBehaviour && isSticky ? null : (
                  <div className={styles.ctas}>
                    {renderPrimaryCta()}
                    {renderSecondaryCta()}
                  </div>
                )}
                {useStickyBehaviour && isSticky && isMobile && (
                  <div className={classnames(styles.stickyCtas)}>
                    {renderPrimaryCta()}
                    {renderSecondaryCta()}
                  </div>
                )}
              </Container>
            </div>
          </div>
        </div>
        {!isMobile && stickyLinkBar && (
          <StickySectionHeader
            {...stickyLinkBar}
            noMarginBottom={noMarginBottom}
            parentPosition={
              normalContentRef?.current?.getBoundingClientRect().top
            }
            parentHeight={
              normalContentRef?.current?.getBoundingClientRect().height
            }
            primaryCta={renderPrimaryCta()}
            secondaryCta={renderSecondaryCta()}
            renderCtas={!isMobile && isSticky}
          />
        )}
      </div>
    )
  }

  return (
    <>
      {renderContent(false)}

      {showSticky && useStickyBehaviour && renderContent(true)}
    </>
  )
}
